//#region libraries
import React, { useEffect, useState } from "react";

// custom components
import { Card, CustomSpinner } from "_atoms";
import { CircleText } from "_components/molecules";

// redux
import { useSelector } from "react-redux";
import { selectMonthlyForecast, selectGetBookingForecastOutboundStatus, selectGetBookingForecastInboundStatus, selectCountOutboundForecast, selectCountInboundForecast, selectGetBookingInboundStatus, selectGetBookingOutboundStatus } from "_redux/slices/prm-bookings/bookings-count";
import { selectSeasonalVarianceFilter } from 'redux/slices/prm-bookings/calendar';
import { selectSeasonalVariances } from '_redux/slices/prm-bookings/seasonal-variance';

// exterior libraries
import { Block, Flex, Text, Title as TremorTitle, Metric, Bold, List, ListItem } from "@tremor/react";
import { Button } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { FaCirclePlus } from "react-icons/fa6";
import { Spacing } from "_styles";
import { Colors } from "_styles";
//#endregion

const BookingForecast = (props) => {

  const history = useHistory();


  //#region redux hooks
  // counts / forecasting
  const monthlyForecast = useSelector(selectMonthlyForecast);
  const countOutboundForecast = useSelector(selectCountOutboundForecast);
  const countInboundForecast = useSelector(selectCountInboundForecast);

  const forecastInboundStatus = useSelector(selectGetBookingForecastInboundStatus);
  const forecastOutboundStatus = useSelector(selectGetBookingForecastOutboundStatus);

  const seasonalVarianceFilter = useSelector(selectSeasonalVarianceFilter);
  const seasonalVariances = useSelector(selectSeasonalVariances);

  const bookingInboundStatus = useSelector(selectGetBookingInboundStatus);
  const bookingOutboundStatus = useSelector(selectGetBookingOutboundStatus);

  //#endregion

  //#region local states
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0);
  const [forecastTotal, setForecastTotal] = useState(0);
  const [departureForecast, setDepartureForecast] = useState(0);
  const [arrivalForecast, setArrivalForecast] = useState(0);

  //#endregion

  //#region use effects

  // calculate seasonal variance
  useEffect(() => {

    let tempOutboundForecast = { ...countOutboundForecast };
    let tempInboundForecast = { ...countInboundForecast };

    // check if any of the date ranges in the seasonal variance matches any dates in the forecast
    if (seasonalVariances && seasonalVariances.seasonalAdjustments) {

      // seasonal variances exist, check each one
      seasonalVariances.seasonalAdjustments.forEach((variance) => {

        // get date range of seasonal variance
        let startDate = new Date(variance.startDate);
        let endDate = new Date(variance.endDate);

        // check if the forecast falls within the seaonal variance date range
        if (tempOutboundForecast) {

          // check date of each forecast value for month
          Object.keys(tempOutboundForecast).forEach((date) => {

            let forecastDate = new Date(date);

            // check if within date range of variance
            if (forecastDate >= startDate && forecastDate <= endDate) {

              // apply variance to forecast
              tempOutboundForecast[date] = countOutboundForecast[date] + (countOutboundForecast[date] * variance.multiplier)
            }
          })
        }

        if (tempInboundForecast) {

          // check date of each forecast value for month
          Object.keys(tempInboundForecast).forEach((date) => {

            let forecastDate = new Date(date);

            // check if within date range of variance
            if (forecastDate >= startDate && forecastDate <= endDate) {

              // apply variance to forecast
              tempInboundForecast[date] = countInboundForecast[date] + (countInboundForecast[date] * variance.multiplier);
            }
          })
        }

      })


      // calculate total
      let total = 0;
      let depTotal = 0;
      let arrivalTotal = 0;

      Object.keys(tempOutboundForecast).forEach((date) => {
        total += tempOutboundForecast[date];
        depTotal += tempOutboundForecast[date];
      });
      Object.keys(tempInboundForecast).forEach((date) => {
        total += tempInboundForecast[date];
        arrivalTotal += tempInboundForecast[date];
      });
      setForecastTotal(Math.round(total));
      setDepartureForecast(Math.round(depTotal));
      setArrivalForecast(Math.round(arrivalTotal));
    }

  }, [seasonalVariances, countOutboundForecast, countInboundForecast]);

  // calculate total forecast
  useEffect(() => {
    if (monthlyForecast && monthlyForecast.departures && monthlyForecast.arrivals) {
      setTotal(monthlyForecast?.departures + monthlyForecast?.arrivals);
    } else {
      setTotal(monthlyForecast?.departures || 0 + monthlyForecast?.arrivals || 0);
    }
  }, [monthlyForecast]);

  useEffect(() => {
    if (forecastInboundStatus == 'loading' || forecastOutboundStatus == 'loading') {
      setLoading(true)
    }
    else {
      setLoading(false)
    }
  }, [forecastInboundStatus, forecastOutboundStatus]);

  //#endregion


  return (
    <Card maxWidth="max-w-lg" shadow={true} decoration="top" decorationColor={"blue"}>
      <div>

        {/* total forecasted bookings */}
        <TremorTitle>{'Total Forecasted Bookings'}</TremorTitle>

        <Block>

          <Flex>

            {/* total counts */}
            {loading ? <CustomSpinner size={"small"} /> : <Metric>{seasonalVarianceFilter && seasonalVariances?.seasonalAdjustments ? forecastTotal.toLocaleString() : total.toLocaleString()}</Metric>}

            {/* add seasonal variance*/}
            <FaCirclePlus
              onClick={() => history.push("/seasonal-variances")}
              style={{ marginLeft: Spacing.SCALE_8, cursor: "pointer" }}
              color={"lightgrey"}
              size={20}
            />

          </Flex>

          {/* list headers */}
          <Flex marginTop="mt-3">
            <Text><Bold>{"Type"}</Bold></Text>
            <Text><Bold>{"Forecast"}</Bold></Text>
          </Flex>

          {/* list */}
          <List>
            <ListItem>
              <Flex>
                {/* <Text truncate={true}>{"Departures"}</Text> */}
                <CircleText
                  value={"Departures"}
                  color={"white"}
                  textColor={Colors.PRM_LIGHT_BLUE}
                  isLoading={bookingOutboundStatus == 'loading'}
                  margin={true}
                />
                <Text>{loading ? <CustomSpinner size={"small"} /> : <>{seasonalVarianceFilter && seasonalVariances?.seasonalAdjustments ? departureForecast.toLocaleString() : monthlyForecast?.departures.toLocaleString()}</>}</Text>
              </Flex>
            </ListItem>
            <ListItem>
              <Flex>
                {/* <Text truncate={true}>{"Arrivals"}</Text> */}
                <CircleText
                  value={"Arrivals"}
                  color={"white"}
                  textColor={"green"}
                  isLoading={bookingInboundStatus == 'loading'}
                  margin={true}
                />
                <Text>{loading ? <CustomSpinner size={"small"} /> : <>{seasonalVarianceFilter && seasonalVariances?.seasonalAdjustments ? arrivalForecast.toLocaleString() : monthlyForecast?.arrivals.toLocaleString()}</>}</Text>
              </Flex>
            </ListItem>
          </List>

        </Block>

      </div>
    </Card>
  );
};

export default BookingForecast;